import { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import AdminLayout from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Calendar, 
  Clock,
  Phone,
  MessageSquare,
  Video,
  CheckCircle,
  XCircle,
  Loader
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusOptions = ['pending', 'confirmed', 'completed', 'cancelled'];

export default function AdminSessions() {
  const { currentTheme } = useTheme();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${API}/admin/sessions`, { withCredentials: true });
      setSessions(response.data || []);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (sessionId, status) => {
    try {
      const formData = new FormData();
      formData.append('status', status);
      
      await axios.put(`${API}/admin/sessions/${sessionId}/status`, formData, { withCredentials: true });
      toast.success('Session status updated');
      fetchSessions();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return '#2A9D8F';
      case 'pending': return '#E9C46A';
      case 'completed': return currentTheme.primary;
      case 'cancelled': return '#E76F51';
      default: return currentTheme.muted;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed': return CheckCircle;
      case 'pending': return Loader;
      case 'completed': return CheckCircle;
      case 'cancelled': return XCircle;
      default: return Clock;
    }
  };

  const getSessionIcon = (type) => {
    switch (type) {
      case 'phone': return Phone;
      case 'chat': return MessageSquare;
      case 'video': return Video;
      default: return Phone;
    }
  };

  const filteredSessions = filter === 'all' 
    ? sessions 
    : sessions.filter(s => s.status === filter);

  return (
    <AdminLayout title="Coaching Sessions">
      <div className="flex justify-between items-center mb-6">
        <p className="font-body opacity-70">
          Manage coaching session requests and bookings.
        </p>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[180px]" data-testid="session-filter">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sessions</SelectItem>
            {statusOptions.map((status) => (
              <SelectItem key={status} value={status} className="capitalize">{status}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div 
        className="rounded-2xl overflow-hidden"
        style={{ backgroundColor: 'white', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}
      >
        {loading ? (
          <div className="p-6 space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="skeleton h-16 rounded-xl" />
            ))}
          </div>
        ) : filteredSessions.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Topic</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Date & Time</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSessions.map((session) => {
                const StatusIcon = getStatusIcon(session.status);
                const SessionIcon = getSessionIcon(session.session_type);
                return (
                  <TableRow key={session.session_id} data-testid={`session-row-${session.session_id}`}>
                    <TableCell>
                      <span className="font-medium">{session.user_name}</span>
                    </TableCell>
                    <TableCell>
                      <span className="line-clamp-1">{session.topic}</span>
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1 capitalize">
                        <SessionIcon className="w-4 h-4" />
                        {session.session_type}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4 opacity-50" />
                          {session.preferred_date}
                        </span>
                        <span className="flex items-center gap-1 text-sm opacity-60">
                          <Clock className="w-3 h-3" />
                          {session.preferred_time}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span 
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium capitalize"
                        style={{ 
                          backgroundColor: `${getStatusColor(session.status)}20`,
                          color: getStatusColor(session.status)
                        }}
                      >
                        <StatusIcon className="w-3 h-3" />
                        {session.status}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Select 
                        value={session.status} 
                        onValueChange={(v) => updateStatus(session.session_id, v)}
                      >
                        <SelectTrigger className="w-[130px]" data-testid={`update-status-${session.session_id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {statusOptions.map((status) => (
                            <SelectItem key={status} value={status} className="capitalize">
                              {status}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        ) : (
          <div className="p-12 text-center">
            <Calendar className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <h3 className="font-display text-xl font-medium mb-2" style={{ color: currentTheme.foreground }}>
              No sessions found
            </h3>
            <p className="font-body opacity-60">
              {filter !== 'all' ? 'Try changing your filter' : 'No coaching sessions yet'}
            </p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
